/**
 * lib/pult-pads.ts — the eight pads of a deck: the words over them, which of them are lit, the
 * pages PARAMETER turns, and what a finger does in HOT CUE (task 60); the lengths of loops, rolls
 * and jumps in beats (task 62).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Пады (второй пакет)»; stories 179, 182–185.
 * Pure: a deck's mode, page and cues in, numbers and words out. The screen does what they say
 * (`screens/Pult.tsx`), the cues live in `store/cues.ts`.
 *
 * ## The pads and their slots
 *
 * The device numbers its pads 1–4 along the upper row and 5–8 along the lower, and HOT CUE gives
 * pad N the track's cue N. The screen over the pads has four cells, one over each column: the
 * upper row's word on top, the lower's below — as the generator draws `1 INTRO` over `5 —`.
 *
 * ## HOT CUE
 *
 * - **A pad with a cue** — a jump to it, the deck playing or not: playing it plays on from there, a
 *   pause stays a pause.
 * - **An empty pad on a pause** — a cue where the deck stands. During play an empty pad does
 *   nothing: the design sets a cue on a pause only (on the device it would set one in play too).
 * - **A long press** — the cue the pad had taken away ({@link PAD_HOLD_MS}): the second action of a
 *   key, where the device had SHIFT. The finger landed first, so the jump has already happened.
 * - **A deck whose cues are not read yet** does nothing at all (`store/cues.ts`, «Read when a deck
 *   takes the track»); the screen asks for the read again (`screens/Pult.tsx`).
 *
 * ## PARAMETER
 *
 * Turns the pages of a mode ({@link PAD_PAGES}), two at most. HOT CUE has one: its eight cues are
 * the eight pads, and the device's manual gives PARAMETER no work in that mode. ROLL, SAMPLER and
 * LOOP have two. SAMPLER's pads get their work and words from task 61 — until then they are
 * numbered by the page, 1–8 and 9–16, so that the page turned shows.
 *
 * ## ROLL, LOOP and the beats (task 62)
 *
 * A ROLL pad held repeats a piece of the track its length long; a LOOP pad sets a loop its length
 * long. Each pad is a length in beats ({@link PAD_BEATS}); the second page is longer, the device's
 * PARAMETER shifting the range. The pads are lit while the deck can loop — a track whole in the
 * voice's memory, and the deck playing; otherwise they are dark, and a finger does nothing. A beat
 * is the track's own ({@link beatSecondsOf}): its sure measure, or 95 BPM — in seconds of the track,
 * so a loop played at another speed is still its beats long by the ear.
 */

import { formatCountdown } from './format';
import type { TrackBeat } from './api';
import { deckBpm } from './pult-effects';
import type { PadMode } from './pult-layout';

/** The pads of a deck, and so the cues of a track. */
export const PAD_COUNT = 8;

/**
 * A finger held this long on a pad is a long press. A second, not the half of a phone's own long
 * press: in HOT CUE it takes a cue away for good, and a finger resting on a pad after a jump must
 * not do that by accident.
 */
export const PAD_HOLD_MS = 1000;

/** The pages PARAMETER turns in each mode — see «PARAMETER». */
export const PAD_PAGES: Readonly<Record<PadMode, number>> = Object.freeze({
  hotcue: 1,
  roll: 2,
  sampler: 2,
  loop: 2,
});

/** Eight slots, pad 1 first: the second of a cue, or `null` for a pad with none. */
export type CueSlots = readonly (number | null)[];

/** What a finger landing on a pad does in HOT CUE. */
export type HotCueAction =
  | { readonly kind: 'jump'; readonly positionS: number }
  | { readonly kind: 'set'; readonly slot: number; readonly positionS: number }
  | { readonly kind: 'none' };

/** Task 62: each mode's pads as lengths in beats, page by page, pad 1 first. */
export const PAD_BEATS: Readonly<Partial<Record<PadMode, readonly (readonly number[])[]>>> =
  Object.freeze({
    roll: [
      [1 / 32, 1 / 16, 1 / 8, 1 / 4, 1 / 2, 1, 2, 4],
      [1 / 8, 1 / 4, 1 / 2, 1, 2, 4, 8, 16],
    ],
    loop: [
      [1 / 8, 1 / 4, 1 / 2, 1, 2, 4, 8, 16],
      [1 / 2, 1, 2, 4, 8, 16, 32, 64],
    ],
  });

/** Task 62: LOOP ON/OFF's loop at every launch, in beats, as the device's. */
export const LOOP_BEATS = 4;

/** Task 62: ½ and 2× keep a loop within these, in beats. */
const LOOP_SHORTEST = 1 / 32;
const LOOP_LONGEST = 64;

/** Task 62: JUMP SIZE's steps, in beats, left to right. */
const JUMP_STEPS: readonly number[] = Object.freeze([1, 2, 4, 8, 16]);

/** Task 62: one beat of the track, in seconds of the track — its sure measure, or 95 BPM. */
export function beatSecondsOf(beat: TrackBeat | null | undefined): number {
  // The effects' own measure at the speed of 1: the track's beats, not the deck's.
  return 60 / deckBpm(beat, 1);
}

/** Task 62: a length in beats as the screen writes it — `1/8`, `1/2`, `4`. */
export function beatsText(beats: number): string {
  return beats < 1 ? `1/${String(Math.round(1 / beats))}` : String(beats);
}

/** Task 62: the beats of pad `pad` (1–8) of the mode's page; `undefined` for a mode of no lengths. */
export function padBeats(mode: PadMode, page: number, pad: number): number | undefined {
  return PAD_BEATS[mode]?.[page]?.[pad - 1];
}

/** Task 62: JUMP SIZE at `position` (0…1) — its nearest step, in beats; the middle is 4. */
export function jumpBeatsAt(position: number): number {
  const share = Number.isFinite(position) ? Math.min(1, Math.max(0, position)) : 0.5;
  return JUMP_STEPS[Math.round(share * (JUMP_STEPS.length - 1))] ?? 4;
}

/** Task 62: `beats` halved (`0.5`) or doubled (`2`), within the loop's shortest and longest. */
export function loopBeatsTimes(beats: number, factor: 0.5 | 2): number {
  return Math.min(LOOP_LONGEST, Math.max(LOOP_SHORTEST, beats * factor));
}

/** A second kept to the millisecond, as the server keeps a cue. */
export function toMillisecond(seconds: number): number {
  return Math.round(seconds * 1000) / 1000;
}

/** The page after `page` one way or the other, within the pages of `mode`. */
export function turnPage(mode: PadMode, page: number, direction: -1 | 1): number {
  return Math.min(PAD_PAGES[mode] - 1, Math.max(0, page + direction));
}

/** The second of pad `pad`'s cue, if the deck's cues are read and it has one. */
function cueOf(cues: CueSlots | undefined, pad: number): number | null {
  return cues?.[pad - 1] ?? null;
}

/** The word over pad `pad` (1–8) of the mode's page. */
function padWord(mode: PadMode, page: number, pad: number, cues: CueSlots | undefined): string {
  if (mode === 'hotcue') {
    const at = cueOf(cues, pad);
    return `${String(pad)} ${at === null ? '—' : formatCountdown(at)}`;
  }
  const beats = padBeats(mode, page, pad);
  return beats === undefined ? `${String(page * PAD_COUNT + pad)} —` : beatsText(beats);
}

/** The screen over the pads: four cells, the upper row's word over the lower's. */
export function padCells(
  mode: PadMode,
  page: number,
  cues: CueSlots | undefined,
): readonly (readonly [string, string])[] {
  return [1, 2, 3, 4].map(
    (pad) => [padWord(mode, page, pad, cues), padWord(mode, page, pad + 4, cues)] as const,
  );
}

/**
 * The pads lit, by number: in HOT CUE those holding a cue; in ROLL and LOOP all of them while the
 * deck can loop (`loopable`, task 62), none otherwise.
 */
export function litPads(
  mode: PadMode,
  cues: CueSlots | undefined,
  loopable: boolean,
): readonly number[] {
  const all = Array.from({ length: PAD_COUNT }, (_, i) => i + 1);
  if (mode === 'hotcue')
    return cues === undefined ? [] : all.filter((pad) => cueOf(cues, pad) !== null);
  if (PAD_BEATS[mode] !== undefined) return loopable ? all : [];
  return [];
}

/** A finger on pad `pad` in HOT CUE, the deck's cues read and the deck where it stands — see «HOT CUE». */
export function hotCuePress(
  cues: CueSlots,
  pad: number,
  deck: { readonly positionS: number; readonly sounding: boolean },
): HotCueAction {
  const at = cueOf(cues, pad);
  if (at !== null) return { kind: 'jump', positionS: at };
  if (deck.sounding) return { kind: 'none' };
  return { kind: 'set', slot: pad, positionS: deck.positionS };
}
